import { defineStore } from 'pinia';
import MutationCall from '~/graphql/wrappers/mutation-call';
import  {
  AcceptCourseCollaborationInviteDocument,
  type AcceptCourseCollaborationInviteMutation,
  type AcceptCourseCollaborationInviteMutationVariables,
  CreateCourseCollaborationInviteDocument,
  type CreateCourseCollaborationInviteMutation,
  type CreateCourseCollaborationInviteMutationVariables,
  InvalidateCourseCollaborationInviteDocument,
  type InvalidateCourseCollaborationInviteMutation,
  type InvalidateCourseCollaborationInviteMutationVariables,
  RemoveCourseCollaboratorDocument,
  type RemoveCourseCollaboratorMutation,
  type RemoveCourseCollaboratorMutationVariables,
} from '~/graphql';
import {
  createPersistedCode,
  PENDING_INVITE_CODE_TTL_MS,
} from '../utils/persisted-code';

let createCourseCollaborationMutation: MutationCall<
  CreateCourseCollaborationInviteMutation,
  CreateCourseCollaborationInviteMutationVariables
>;
let invalidateCourseCollaborationMutation: MutationCall<
  InvalidateCourseCollaborationInviteMutation,
  InvalidateCourseCollaborationInviteMutationVariables
>;
let acceptCourseCollaborationMutation: MutationCall<
  AcceptCourseCollaborationInviteMutation,
  AcceptCourseCollaborationInviteMutationVariables
>;
let removeCourseCollaboratorMutation: MutationCall<
  RemoveCourseCollaboratorMutation,
  RemoveCourseCollaboratorMutationVariables
>;

// Persisted rather than kept in store state: the signup it waits for can finish
// in a fresh tab (the link in the verification e-mail) or after a provider
// round trip.
const codeToAcceptAfterLogin = createPersistedCode(
  'course_invite_code_to_accept',
  PENDING_INVITE_CODE_TTL_MS,
);

export const useCourseCollaborationManagement = defineStore('courseCollaborationManagement', {
  getters: {},
  actions: {
    setCodeToAcceptAfterLogin(code: string) {
      codeToAcceptAfterLogin.write(code);
    },
    async acceptInviteAfterLogin() {
      const code = codeToAcceptAfterLogin.read();
      if (code === undefined) {
        return;
      }
      // Taken before the attempt, so a code that fails (expired, revoked) is
      // not retried - and thrown again - on every later load of the user.
      codeToAcceptAfterLogin.clear();
      await this.acceptCourseCollaborationInvite(code);
    },
    async init() {
      createCourseCollaborationMutation = new MutationCall(CreateCourseCollaborationInviteDocument);
      invalidateCourseCollaborationMutation = new MutationCall(
        InvalidateCourseCollaborationInviteDocument
      );
      acceptCourseCollaborationMutation = new MutationCall(AcceptCourseCollaborationInviteDocument);
      removeCourseCollaboratorMutation = new MutationCall(RemoveCourseCollaboratorDocument);
    },
    async createCourseCollaborationInvite(id: string) {
      const result = await createCourseCollaborationMutation.run({
        courseIdInput: {
          id,
        },
      });
      if (result.hasError()) {
        console.log(result.getError());
        throw result.getError();
      }
      return result.getData().createCourseCollaborationInvite.courseInvite;
    },
    async invalidateCourseCollaborationInvite(id: string) {
      const result = await invalidateCourseCollaborationMutation.run({
        courseIdInput: {
          id,
        },
      });

      if (result.hasError()) {
        console.log(result.getError());
        throw result.getError();
      }

      return;
    },
    async removeCourseCollaborator(courseId: string, collaboratorId: string) {
      const result = await removeCourseCollaboratorMutation.run({
        removeCourseCollaboratorInput: {
          collaboratorId,
          courseId,
        },
      });

      if (result.hasError()) {
        console.log(result.getError());
        throw result.getError();
      }

      return;
    },
    async acceptCourseCollaborationInvite(code: string) {
      const result = await acceptCourseCollaborationMutation.run({
        codeInput: {
          code,
        },
      });

      if (result.hasError()) {
        console.log(result.getError());
        throw result.getError();
      }

      return result.getData().acceptCourseCollaborationInvite.course;
    },
  },
});
